import React, { useState } from 'react';
import { LogEntry } from './CPRLog';
import Modal, { ModalDirectionOptions } from '../components/Modal';
import { useTranslation } from 'react-i18next';
import './EntryDialog.scss';

interface EntryDialogProps {
  entry?: LogEntry;
  type: LogEntry['type'];
  onSave: (entry: Omit<LogEntry, 'id' | 'type'>) => void;
  onClose: () => void;
}

const EntryDialog: React.FC<EntryDialogProps> = ({ entry, type, onSave, onClose }) => {
  const { t } = useTranslation(['resus']);
  // Initialize with local date string if entry exists, otherwise current local time
  const [timestamp, setTimestamp] = useState(() => {
    if (entry?.timestamp) {
      // Convert existing ISO string to local date
      const date = new Date(entry.timestamp);
      return date.toISOString();
    }
    return new Date().toISOString();
  });
  const [text, setText] = useState(entry?.text || '');

  // Convert ISO string to local datetime-local format
  const getLocalDateTimeString = (isoString: string) => {
    const date = new Date(isoString);
    // Adjust for local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Handle datetime-local input change
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const localDate = new Date(e.target.value);
    setTimestamp(localDate.toISOString());
  };

  const handleSave = () => {
    onSave({
      timestamp,
      text,
      isImportant: entry ? entry.isImportant : false
    });
  };

  const getTitle = () => {
    const action = entry ? t('entryDialog.editPrefix') : t('entryDialog.addPrefix');
    switch (type) {
      case 'medication':
        return `${action} ${t('entryDialog.medicationSuffix')}`;
      case 'openingConditions':
        return `${action} ${t('entryDialog.patientInfoSuffix')}`;
      case 'action':
        return `${action} ${t('entryDialog.actionSuffix')}`;
    }
  };

  return (
    <Modal
      isOpen={true}
      setIsOpen={() => onClose()}
      title={getTitle()}
      primaryButton={{
        text: t('buttons.save'),
        onClick: handleSave
      }}
      secondaryButton={{
        text: t('buttons.cancel'),
        onClick: onClose
      }}
      direction={ModalDirectionOptions.RTL}
    >
      <div className="cpr-entry-dialog">
        <label>
          {t('entryDialog.timeLabel')}
          <input
            type="datetime-local"
            value={getLocalDateTimeString(timestamp)}
            onChange={handleDateChange}
          />
        </label>
        <label>
          {t('entryDialog.contentLabel')}
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
      </div>
    </Modal>
  );
};

export default EntryDialog;
